import { NGXLogger } from 'ngx-logger';
import { AsyncPipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink, RouterModule } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { Observable, Subject, take, takeUntil } from 'rxjs';
import { Group } from '../../../../models/group.model';
import { Member } from '../../../../models/member.model';
import {
  MembershipStatus,
  MemberType,
} from '../../../../models/membership.status';
import { InitialsPipe } from '../../../../pipes/initials.pipe';
import { MomentPipe } from '../../../../pipes/moment.pipe';
import { UtilsService } from '../../../../services/utils.service';
import { AppExceptionModel } from '../../../../models/app-exception.model';
import * as groupActions from '../../../../state/actions/group.actions';
import * as memberActions from '../../../../state/actions/member.actions';
import { AppState } from '../../../../state/appstate';
import * as groupSelector from '../../../../state/selectors/group.selector';
import * as memberSelector from '../../../../state/selectors/member.selector';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { BtnChipComponent } from '../../../components/buttons/btn-chip/btn-chip.component';
import { BtnMemberStatusComponent } from '../../../components/buttons/btn-member-status/btn-member-status.component';
import { BtnRouteComponent } from '../../../components/buttons/btn-route/btn-route.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ChipComponent, ChipColor } from '../../../components/inputs/chip/chip.component';
import { DlgChangeStatusComponent } from '../dlg-change-status/dlg-change-status.component';
import { BtnFabComponent } from '../../../components/buttons/btn-fab/btn-fab.component';
import { PullToRefreshComponent } from '../../../components/pull-to-refresh/pull-to-refresh/pull-to-refresh.component';

@Component({
  selector: 'app-list-member',
  standalone: true,
  imports: [
    RouterModule,
    AsyncPipe,
    RouterLink,
    BtnBackComponent,
    BtnRouteComponent,
    NgxSkeletonLoaderModule,
    BtnMemberStatusComponent,
    BtnChipComponent,
    MomentPipe,
    InitialsPipe,
    ChipComponent,
    BtnFabComponent,
    PullToRefreshComponent,
  ],
  templateUrl: './list-member.component.html',
  styleUrls: ['./list-member.component.css'],
})
export class ListMemberComponent implements OnInit, OnDestroy {
  activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private router: Router = inject(Router);
  private logger: NGXLogger = inject(NGXLogger);
  private dialog: MatDialog = inject(MatDialog);
  private utilsService: UtilsService = inject(UtilsService);

  members$: Observable<Member[] | null | undefined> = new Observable();

  loading$: Observable<boolean> = new Observable();
  groupStatsLoading$: Observable<boolean> = new Observable();

  memberStatsLoading: boolean = true;

  groupId: string | null = this.activatedRoute.snapshot.paramMap.get('id');

  group: Group | null | undefined;

  myRoles$: Observable<any> = new Observable();

  seatsLimit: number = 100;
  isSubscribed: boolean = false;

  membershipStatus = MembershipStatus;
  memberType = MemberType;

  chipColors: Record<MembershipStatus, ChipColor> = {
    [MembershipStatus.active]: ChipColor.SUCCESS,
    [MembershipStatus.requested]: ChipColor.WARNING,
    [MembershipStatus.invited]: ChipColor.INFO,
    [MembershipStatus.rejected]: ChipColor.DANGER,
    [MembershipStatus.removed]: ChipColor.DANGER,
    [MembershipStatus.alumni]: ChipColor.SECONDARY,
    [MembershipStatus.unknown]: ChipColor.PRIMARY,
  };
  chipColorAdmin = ChipColor.DARK;
  chipColorLight = ChipColor.LIGHT;

  exit$: Subject<void> = new Subject();

  constructor(private store: Store<AppState>) {
    this.activatedRoute.paramMap
      .pipe(takeUntil(this.exit$))
      .subscribe((params) => {
        this.groupId = params.get('id');
      });
    if (this.groupId != null) {
      // Load Group members from Store
      this.members$ = this.store.pipe(
        select(memberSelector.getMembersByGroupId(this.groupId!)),
      );

      this.store
        .select((state) => state.members)
        .pipe(takeUntil(this.exit$))
        .pipe(select(memberSelector.memberStatsLoading))
        .subscribe((loading) => {
          this.memberStatsLoading = loading;
        });

      this.store
        .pipe(select(groupSelector.getGroupById(this.groupId!)))
        .pipe(takeUntil(this.exit$))
        .subscribe((group) => {
          if (group == undefined) return;
          this.group = group;
          if (group?.adminMembers == undefined) {
            this.store.dispatch(
              groupActions.getGroupStats({ groupId: this.groupId! }),
            );
          }
          if (group?.newMembers == undefined && !this.memberStatsLoading) {
            this.store.dispatch(
              memberActions.getMemberStats({ groupId: this.groupId! }),
            );
          }
        });

      const defaultFreeSeats = 100;
      this.store
        .select(groupSelector.getGroupMetadataMap)
        .pipe(takeUntil(this.exit$))
        .subscribe((metadataMap) => {
          const metadata = metadataMap ? metadataMap[this.groupId!] : null;
          if (metadata) {
            this.seatsLimit = metadata.subscribedSeatsTier ?? defaultFreeSeats;
            this.isSubscribed = this.seatsLimit > defaultFreeSeats;
          } else {
            this.seatsLimit = defaultFreeSeats;
            this.isSubscribed = false;
          }
        });
    }

    this.myRoles$ = this.store.select(
      groupSelector.getMyRolesInGroup(this.groupId),
    );

    this.loading$ = this.store
      .select((state) => state.members)
      .pipe(select(memberSelector.loading));

    this.groupStatsLoading$ = this.store
      .select((state) => state.groups)
      .pipe(select(groupSelector.statsLoading));
  }

  ngOnInit() {
    this.members$.pipe(take(1)).subscribe((members) => {
      if (members?.length == 0) {
        this.store.dispatch(
          memberActions.getMembers({
            groupId: this.groupId!,
            memberType: (this.group?.memberType as
              | MemberType[]
              | undefined) ?? [MemberType.member],
            lastRecord: undefined,
          }),
        );
      }
    });
  }

  onRefresh() {
    this.store.dispatch(memberActions.clear());
    this.store.dispatch(
      memberActions.getMembers({
        groupId: this.groupId!,
        memberType: (this.group?.memberType as
          | MemberType[]
          | undefined) ?? [MemberType.member],
        lastRecord: undefined,
      }),
    );
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  addMore() {
    this.members$.pipe(take(1)).subscribe((members) => {
      if (members != undefined) {
        this.store.dispatch(
          memberActions.getMembers({
            groupId: this.groupId!,
            memberType: (this.group?.memberType as
              | MemberType[]
              | undefined) ?? [MemberType.member],
            lastRecord:
              members!.length > 0
                ? members[members!.length - 1].modified
                : undefined,
          }),
        );
      }
    });
  }

  onImportClick(): void {
    this.logger.debug('list-member.component.ts :: Import clicked:', { groupId: this.groupId });
    this.router.navigate(['/groups', this.groupId, 'import']);
  }

  onAddMemberClick() {
    const currentMembers = this.group?.activeMembers ?? 0;
    if (currentMembers >= this.seatsLimit) {
      let title = 'Free Tier Limit Reached';
      let message = 'Adding a member requires upgrading to a paid plan. Please upgrade your plan in the Billing tab to add more members.';

      if (this.isSubscribed) {
        title = 'Plan Limit Reached';
        message = `Adding a member exceeds your current plan limit of ${this.seatsLimit} seats. Please upgrade your plan in the Billing tab to add more members.`;
      }

      this.utilsService.onShowAlert(
        {
          title,
          message,
        } as AppExceptionModel,
        false,
      );
      return;
    }

    this.router.navigate(['create'], { relativeTo: this.activatedRoute });
  }

  onChangeStatus(item: Member) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      title: 'Update Member Status',
      description: `Select status to assign to '${item.name}'`,
      action: 'Update',
      member: item,
    };
    const dialogRef = this.dialog.open(
      DlgChangeStatusComponent,
      dialogConfig
    );
    return dialogRef.afterClosed().subscribe((result) => {
      return;
    });
  }
}
